/**
 * Lead Machine landing popups stay in the DOM with aria-hidden="true"
 * while Close and form controls remain focusable. Axe / Lighthouse fail
 * aria-hidden-focus for that. While hidden: inert + tabindex=-1 on
 * focusable descendants. Restore when the popup is shown.
 */

const FOCUSABLE =
	'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, iframe, object, embed, [contenteditable="true"], [tabindex]';

const TAB_STASH = 'data-dc-ddlm-tab';

/** @type {WeakSet<Element>} */
const observed = new WeakSet();

/**
 * @param {Element} popup
 */
function isPopupHidden(popup) {
	if (popup.hasAttribute('hidden')) {
		return true;
	}
	return popup.getAttribute('aria-hidden') === 'true';
}

/**
 * @param {Element} el
 */
function stashTabIndex(el) {
	if (!(el instanceof HTMLElement) || el.hasAttribute(TAB_STASH)) {
		return;
	}
	const current = el.getAttribute('tabindex');
	el.setAttribute(TAB_STASH, current === null ? '' : current);
	el.setAttribute('tabindex', '-1');
}

/**
 * @param {Element} el
 */
function restoreTabIndex(el) {
	if (!(el instanceof HTMLElement) || !el.hasAttribute(TAB_STASH)) {
		return;
	}
	const previous = el.getAttribute(TAB_STASH);
	el.removeAttribute(TAB_STASH);
	if (previous === null || previous === '') {
		el.removeAttribute('tabindex');
		return;
	}
	el.setAttribute('tabindex', previous);
}

/**
 * @param {Element} popup
 */
function syncPopup(popup) {
	if (!(popup instanceof HTMLElement)) {
		return;
	}

	const focusables = popup.querySelectorAll(FOCUSABLE);

	if (isPopupHidden(popup)) {
		popup.setAttribute('inert', '');
		focusables.forEach(stashTabIndex);
		if (popup.contains(document.activeElement)) {
			document.activeElement.blur();
		}
		return;
	}

	popup.removeAttribute('inert');
	focusables.forEach(restoreTabIndex);
	popup.querySelectorAll(`[${TAB_STASH}]`).forEach(restoreTabIndex);
}

/**
 * @param {Element} popup
 */
function observePopup(popup) {
	if (observed.has(popup)) {
		return;
	}
	observed.add(popup);
	syncPopup(popup);

	let frame = 0;
	const schedule = () => {
		if (frame) {
			return;
		}
		frame = window.requestAnimationFrame(() => {
			frame = 0;
			syncPopup(popup);
		});
	};

	const observer = new MutationObserver(schedule);
	observer.observe(popup, {
		attributes: true,
		attributeFilter: ['aria-hidden', 'hidden', 'class'],
		childList: true,
		subtree: true,
	});
}

/**
 * @param {ParentNode} root
 */
function bindPopups(root) {
	if (root instanceof Element && root.matches('.ddlm-lpop')) {
		observePopup(root);
	}
	root.querySelectorAll?.('.ddlm-lpop').forEach(observePopup);
}

export function initLeadMachineA11y() {
	bindPopups(document);

	const mo = new MutationObserver((mutations) => {
		for (const mutation of mutations) {
			mutation.addedNodes.forEach((node) => {
				if (node instanceof Element) {
					bindPopups(node);
				}
			});
		}
	});

	mo.observe(document.documentElement, { childList: true, subtree: true });
}
